import { useState } from "react";
import { readingThemes } from "@/mocks/siteData";
import SectionHeading from "@/pages/home/components/SectionHeading";

const qualities = ["标清", "高清", "超清"];

const readingPoints = [
  { icon: "ri-settings-3-line", title: "阅读设置可自定义", desc: "字号、行距、翻页方式（上下滚动 / 左右翻页）都能按个人习惯调整。" },
  { icon: "ri-download-cloud-2-line", title: "离线缓存更省心", desc: "提前缓存想看的章节，通勤、地铁、飞机上也能接着读。" },
  { icon: "ri-history-line", title: "进度自动记录", desc: "读到哪一话、哪一页会自动保存，换设备登录同一账号即可继续。" },
  { icon: "ri-eye-line", title: "久看不累眼", desc: "提供米白、豆绿、夜间三套底色，长时间阅读也能保持舒适。" },
];

export default function ReadingExperience() {
  const [themeId, setThemeId] = useState(readingThemes[0].id);
  const [quality, setQuality] = useState("高清");

  const theme = readingThemes.find((t) => t.id === themeId) ?? readingThemes[0];

  return (
    <section id="reading" className="scroll-mt-20 bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          eyebrow="阅读体验"
          title="底色、画质、翻页方式，都交给你决定"
          desc="阅读界面尽量保持干净，把控制权留给读者。下面是一个可交互的阅读预览，你可以直接切换底色和画质，看看不同设置下的大致效果。"
        />

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="reveal">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600">阅读底色：</span>
              {readingThemes.map((t) => {
                const active = t.id === themeId;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setThemeId(t.id)}
                    aria-pressed={active}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    <span className={`h-3 w-3 rounded-full ${t.className} ring-1 ring-black/10`} aria-hidden="true" />
                    {t.name}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600">阅读画质：</span>
              <div className="inline-flex rounded-full border border-background-200 bg-background-50 p-1">
                {qualities.map((q) => {
                  const active = q === quality;
                  return (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setQuality(q)}
                      aria-pressed={active}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                        active ? "bg-secondary-500 text-background-50" : "text-foreground-600 hover:text-secondary-700"
                      }`}
                    >
                      {q}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`mt-6 rounded-lg p-6 transition-colors duration-300 md:p-8 ${theme.className}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs ${theme.text} opacity-70`}>第 128 话 · 归途</span>
                <span className={`text-xs ${theme.text} opacity-70`}>
                  {quality} · 第 12 / 36 页
                </span>
              </div>
              <h3 className={`mt-3 font-heading text-xl ${theme.text}`}>星辰剑歌</h3>
              <p className={`mt-4 text-sm leading-loose ${theme.text} opacity-90`}>
                “你问剑道的尽头有没有答案——问出这句话的人，也许本身就是答案。”
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className={`h-24 rounded-md ${theme.text} opacity-15`} aria-hidden="true" />
                <div className={`h-24 rounded-md ${theme.text} opacity-10`} aria-hidden="true" />
              </div>
              <div className={`mt-3 h-14 rounded-md ${theme.text} opacity-15`} aria-hidden="true" />
              <p className={`mt-4 text-center text-xs ${theme.text} opacity-60`}>
                预览效果仅供示意，实际阅读界面以客户端为准
              </p>
            </div>
          </div>

          <div className="reveal flex flex-col gap-4">
            {readingPoints.map((point) => (
              <div
                key={point.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${point.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-base text-foreground-950">{point.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{point.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}